import React, { useState, useEffect } from 'react';
import { getEntryWindowTimeRemaining, formatTimeRemaining } from '../types/visitor';

interface CountdownTimerProps {
  expiresAt: string;
  label?: string;
  className?: string;
}

/**
 * CountdownTimer Component
 *
 * Displays a live countdown timer that updates every second.
 * Shows warning colors when less than 5 minutes remaining.
 *
 * @param expiresAt - ISO8601 timestamp when the timer expires
 * @param label - Optional label to display before the countdown (default: "Janela:")
 * @param className - Optional additional CSS classes
 */
const CountdownTimer: React.FC<CountdownTimerProps> = ({
  expiresAt,
  label = 'Janela:',
  className = ''
}) => {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(
    getEntryWindowTimeRemaining(expiresAt)
  );

  useEffect(() => {
    // Update countdown every second
    const interval = setInterval(() => {
      const remaining = getEntryWindowTimeRemaining(expiresAt);
      setTimeRemaining(remaining);

      // Stop interval when expired
      if (remaining === null || remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Determine color based on time remaining
  const getColorClass = (): string => {
    if (timeRemaining === null || timeRemaining <= 0) {
      return 'text-red-700 font-bold'; // Expired
    }

    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const TWO_MINUTES_MS = 2 * 60 * 1000;

    if (timeRemaining < TWO_MINUTES_MS) {
      return 'text-red-600 font-bold animate-pulse'; // Critical - less than 2 minutes
    } else if (timeRemaining < FIVE_MINUTES_MS) {
      return 'text-orange-600 font-semibold'; // Warning - less than 5 minutes
    } else {
      return 'text-yellow-700 font-medium'; // Normal
    }
  };

  return (
    <div className={`text-xs whitespace-nowrap ${getColorClass()} ${className}`}>
      {label} {formatTimeRemaining(timeRemaining)}
    </div>
  );
};

export default CountdownTimer;
